import type { ReactNode } from 'react';
import { Check } from '@mui/icons-material';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { fontWeights } from '@/@mui/tokens/font-weights';
import { AnimatedText } from '@/components/shared/animated-text';
import type { ProfileStepIds } from '@/models/recommendation';
import { useProfileQuiz } from '@/packages/profile-quiz/context/profile-quiz';
import { useScrollToEnd } from '@/packages/profile-quiz/context/scroll-to-end';
import { quizAnswerToLabel } from '@/packages/profile-quiz/utils/profile-value-to-label';

export interface BaseQuestionProps {
  children: ReactNode;
  questionTitle: string;
  processedResponseText: string;
  questionId: ProfileStepIds;
}
export const BaseQuestion = ({
  children,
  questionTitle,
  processedResponseText,
  questionId,
}: BaseQuestionProps) => {
  const { goNext, currentQuestionId, submittedQuestionId, answers } =
    useProfileQuiz();
  const { scrollToEnd } = useScrollToEnd();

  const isActive =
    currentQuestionId === questionId && submittedQuestionId !== questionId;

  const shouldAnimate = submittedQuestionId === questionId;

  const isAnswered = currentQuestionId !== questionId;

  const answerLabelTexts = quizAnswerToLabel(questionId, answers);

  return (
    <Box>
      <Typography sx={{ mb: 2 }} fontWeight={fontWeights.fontWeightSemibold}>
        {questionTitle}
      </Typography>
      {isActive && <Stack gap={1}>{children}</Stack>}
      {!isActive && (
        <>
          <Stack gap={1} direction="row" flexWrap="wrap" mb={1}>
            {answerLabelTexts instanceof Array ? (
              answerLabelTexts.map((item, index) => (
                <Chip
                  key={`${questionId}-${index}`}
                  label={item}
                  variant="outlined"
                  color="secondary"
                  icon={<Check fontSize="small" />}
                />
              ))
            ) : (
              <Chip
                key={`${questionId}`}
                label={answerLabelTexts}
                variant="outlined"
                color="secondary"
                icon={<Check fontSize="small" />}
              />
            )}
          </Stack>
          {shouldAnimate && (
            <AnimatedText
              text={processedResponseText}
              isActive={true}
              onEnd={() => {
                goNext();
                scrollToEnd();
              }}
            />
          )}
          {isAnswered && !shouldAnimate && (
            <Typography>{processedResponseText}</Typography>
          )}
        </>
      )}
    </Box>
  );
};
